import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Building2, Users, Award, Webhook, Activity, RefreshCw, Settings, Plus, ExternalLink, ShieldCheck } from "lucide-react";

const INDUSTRIES = ["bank", "insurer", "government", "customs", "revenue_authority", "police", "regulator", "mining", "fleet", "vehicle_manufacturer", "dealer", "auction", "property", "logistics", "commodity_trader", "enterprise", "other"];

/**
 * Partner Workspace — /orgs and /orgs/:orgId
 * List all orgs the user is a member of + full workspace when one is opened:
 * dashboard · certificates · API clients + webhooks · members · settings · audit.
 */
export default function PartnerWorkspace() {
  const { orgId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: "", industry: "bank", country: "Zimbabwe" });

  // list orgs the user can access
  const loadOrgs = async () => {
    try { const r = await api.get("/orgs"); setOrgs(r.data.items || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { loadOrgs(); }, []);

  // load workspace data for the selected org
  const loadWorkspace = async () => {
    if (!orgId) return;
    setBusy(true);
    try {
      const [org, dash, certs, apis, mem, ana, aud] = await Promise.all([
        api.get(`/orgs/${orgId}`),
        api.get(`/orgs/${orgId}/dashboard?period_days=30`),
        api.get(`/orgs/${orgId}/certificates?limit=100`),
        api.get(`/orgs/${orgId}/api-clients`),
        api.get(`/orgs/${orgId}/members`),
        api.get(`/orgs/${orgId}/analytics?days=30`),
        api.get(`/orgs/${orgId}/audit?limit=50`),
      ]);
      setData({ org: org.data, dash: dash.data, certs: certs.data,
                 apis: apis.data, mem: mem.data, ana: ana.data, aud: aud.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load workspace"); }
    finally { setBusy(false); }
  };
  useEffect(() => { loadWorkspace(); /* eslint-disable-next-line */ }, [orgId]);

  const create = async () => {
    if (!newOrg.name.trim()) return toast.error("Name required");
    try {
      const r = await api.post("/orgs", newOrg);
      toast.success(`Created ${r.data.name}`);
      setCreateOpen(false);
      await loadOrgs();
      nav(`/orgs/${r.data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const brand = data?.org?.organisation?.branding || {};
  const accent = brand.accent_colour || "#D4AF37";

  // ─── List view ────────────────────────────────────────────────────
  if (!orgId) {
    return (
      <div>
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-6 py-10">
          <header className="flex items-start justify-between gap-4 flex-wrap mb-8">
            <div>
              <div className="kicker">Enterprise · Partner Workspaces</div>
              <h1 className="display text-4xl">Organisations</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Every enterprise partner receives its own workspace — organisation-scoped dashboards, certificates, API clients, users, analytics and audit.</p>
            </div>
            {["super_admin", "admin"].includes(user?.role) && (
              <Button size="sm" onClick={() => setCreateOpen(!createOpen)} data-testid="new-org-btn"><Plus className="h-3.5 w-3.5 mr-1" /> New organisation</Button>
            )}
          </header>

          {createOpen && (
            <div className="border-2 border-primary/40 bg-primary/5 p-4 mb-6" data-testid="new-org-form">
              <div className="grid md:grid-cols-3 gap-3">
                <Input placeholder="Organisation name" value={newOrg.name} onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })} data-testid="new-org-name" />
                <select className="h-9 border border-border bg-background px-2 text-[12.5px] rounded-sm" value={newOrg.industry} onChange={(e) => setNewOrg({ ...newOrg, industry: e.target.value })}>{INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}</select>
                <Input placeholder="Country" value={newOrg.country} onChange={(e) => setNewOrg({ ...newOrg, country: e.target.value })} />
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={create} data-testid="new-org-submit">Create</Button>
                <Button size="sm" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="orgs-grid">
            {orgs.map(o => (
              <Link key={o.id} to={`/orgs/${o.id}`} className="border border-border bg-card p-4 hover:border-primary transition-colors" data-testid={`org-card-${o.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{o.industry}</div>
                    <div className="text-lg font-semibold mt-0.5 truncate">{o.name}</div>
                    <div className="text-[12px] text-muted-foreground mt-0.5">{o.country} · {(o.country_coverage || []).length} country{(o.country_coverage || []).length !== 1 ? "ies" : ""}</div>
                  </div>
                  <Building2 className="h-5 w-5 opacity-40" style={{ color: o.branding?.accent_colour }} />
                </div>
                <div className="mt-3 flex gap-2 items-center">
                  <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">{o.subscription?.tier}</span>
                  {o.trust_profile?.verified_partner && <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-700">verified partner</span>}
                </div>
              </Link>
            ))}
            {orgs.length === 0 && <div className="col-span-full p-8 text-center text-muted-foreground text-sm border border-border bg-card">No organisations yet. {["super_admin", "admin"].includes(user?.role) && "Click 'New organisation' above."}</div>}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // ─── Workspace view ───────────────────────────────────────────────
  const org = data?.org?.organisation;
  return (
    <div>
      <Navbar />
      {/* Branded header */}
      <div className="border-b border-border" style={{ background: `linear-gradient(90deg, ${accent}12, transparent)` }}>
        <div className="max-w-[1600px] mx-auto px-6 py-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Link to="/orgs" className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary">← All organisations</Link>
            <h1 className="display text-3xl mt-1" data-testid="ws-org-name">{org?.name || "Loading…"}</h1>
            <div className="text-[12.5px] text-muted-foreground mt-1">{org?.industry} · {org?.country} · {(org?.coverage || []).join(" · ")}</div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Button size="sm" variant="outline" onClick={loadWorkspace} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
            <Link to="/admin/api"><Button size="sm" variant="outline">Developer Portal</Button></Link>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {/* Tab strip */}
        <div className="flex border-b border-border mb-6 gap-4 overflow-x-auto">
          {[["dashboard","Dashboard",Activity],["certificates","Certificates",Award],["api","API Clients & Webhooks",Webhook],["members","Users",Users],["analytics","Analytics",Activity],["settings","Settings & Branding",Settings],["audit","Audit",ShieldCheck]].map(([k,l,Ic]) => (
              <button key={k} onClick={() => setTab(k)} className={`px-1 pb-3 text-sm whitespace-nowrap flex items-center gap-1.5 ${tab === k ? "border-b-2 border-primary text-primary font-medium" : "text-muted-foreground"}`} data-testid={`ws-tab-${k}`}><Ic className="h-3.5 w-3.5" /> {l}</button>
          ))}
        </div>

        {tab === "dashboard" && <DashboardTab d={data?.dash} accent={accent} />}
        {tab === "certificates" && <CertsTab rows={data?.certs?.items || []} />}
        {tab === "api" && <ApiTab clients={data?.apis?.oauth_clients || []} webhooks={data?.apis?.webhooks || []} />}
        {tab === "members" && <MembersTab members={data?.mem?.items || []} roles={data?.mem?.org_roles || []} orgId={orgId} onReload={loadWorkspace} />}
        {tab === "analytics" && <AnalyticsTab a={data?.ana} />}
        {tab === "settings" && <SettingsTab org={org} orgId={orgId} onSaved={loadWorkspace} />}
        {tab === "audit" && <AuditTab rows={data?.aud?.items || []} />}
      </div>
      <Footer />
    </div>
  );
}

const K = ({ label, value, sub }) => (
  <div className="border border-border bg-card p-4">
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="text-3xl font-semibold mt-1">{value ?? "—"}</div>
    {sub && <div className="text-[11.5px] text-muted-foreground mt-1">{sub}</div>}
  </div>
);

const DashboardTab = ({ d, accent }) => {
  if (!d) return <div className="p-6 text-muted-foreground text-sm">Loading…</div>;
  return (
    <div>
      <div className="grid md:grid-cols-4 gap-4 mb-6" data-testid="ws-dashboard-kpis">
        <K label="Certificates counter-signed" value={d.counter_signatures.total_certificates} sub={`+${d.counter_signatures.new_in_period} in 30d`} />
        <K label="API requests · 30d" value={d.api.requests} sub={d.api.success_pct != null ? `${d.api.success_pct}% success` : "—"} />
        <K label="Active webhooks" value={d.api.active_webhooks} sub={`${d.api.oauth_clients} OAuth2 clients`} />
        <K label="Members" value={d.members.total} sub={Object.keys(d.members.by_role).length + " role type(s)"} />
      </div>
      <div className="border border-border bg-card p-5">
        <div className="kicker">Executive summary</div>
        <p className="mt-2 text-[14px] leading-relaxed">{d.narrative}</p>
        <div className="mt-3 text-[12px] text-muted-foreground">
          Subscription tier: <strong>{d.organisation.subscription?.tier}</strong> ·
          Status: <strong>{d.organisation.subscription?.status}</strong> ·
          Trust score: <strong>{d.organisation.trust_profile?.score ?? "—"}</strong>
        </div>
      </div>
    </div>
  );
};

const CertsTab = ({ rows }) => (
  <div className="border border-border bg-card overflow-x-auto" data-testid="ws-certs">
    <table className="w-full text-[13px]">
      <thead className="bg-muted/40 text-left">
        <tr><th className="p-2.5">Certificate</th><th className="p-2.5">Asset</th><th className="p-2.5">Country</th><th className="p-2.5">Status</th><th className="p-2.5">Issued</th><th className="p-2.5">Expires</th><th className="p-2.5"></th></tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map(r => (
          <tr key={r.certificate_number} data-testid={`ws-cert-${r.certificate_number}`}>
            <td className="p-2.5 mono text-[11px]">{r.certificate_number}</td>
            <td className="p-2.5">{r.asset_type} · {r.asset_ref}</td>
            <td className="p-2.5">{r.country}</td>
            <td className="p-2.5 mono text-[10.5px] uppercase">{r.status}</td>
            <td className="p-2.5 mono text-[10.5px]">{(r.issued_at || "").slice(0,10)}</td>
            <td className="p-2.5 mono text-[10.5px]">{(r.expires_at || "").slice(0,10) || "—"}</td>
            <td className="p-2.5 text-right"><Link to={`/verify/certificate/${r.certificate_number}`} target="_blank"><Button size="sm" variant="ghost"><ExternalLink className="h-3 w-3" /></Button></Link></td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground text-sm">No certificates counter-signed by this organisation yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

const ApiTab = ({ clients, webhooks }) => (
  <div className="grid lg:grid-cols-2 gap-4" data-testid="ws-api">
    <div className="border border-border bg-card">
      <div className="p-3 border-b border-border kicker">OAuth2 clients</div>
      <div className="divide-y divide-border">
        {clients.map(c => (
          <div key={c.client_id} className="p-3">
            <div className="text-[13px] font-medium">{c.client_id}</div>
            <div className="mono text-[10.5px] uppercase text-muted-foreground">{c.tier} · {c.env} {c.active ? "" : "· revoked"}</div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5 truncate">{(c.scopes || []).join(", ")}</div>
          </div>
        ))}
        {clients.length === 0 && <div className="p-4 text-[12.5px] text-muted-foreground text-center">No OAuth2 clients yet. Ask your Super Admin to provision one in the Developer Portal.</div>}
      </div>
    </div>
    <div className="border border-border bg-card">
      <div className="p-3 border-b border-border kicker">Webhook subscriptions</div>
      <div className="divide-y divide-border">
        {webhooks.map(w => (
          <div key={w.id} className="p-3">
            <div className="text-[13px] font-medium truncate">{w.url}</div>
            <div className="mono text-[10.5px] uppercase text-muted-foreground">{w.env} · streak {w.failure_streak || 0}</div>
            <div className="text-[10.5px] text-muted-foreground mt-0.5">{(w.events || []).join(", ")}</div>
          </div>
        ))}
        {webhooks.length === 0 && <div className="p-4 text-[12.5px] text-muted-foreground text-center">No webhook subscriptions yet.</div>}
      </div>
    </div>
  </div>
);

const MembersTab = ({ members, roles, orgId, onReload }) => {
  const [newUser, setNewUser] = useState({ user_id: "", role: roles[0] || "org_manager" });
  const add = async () => {
    if (!newUser.user_id) return toast.error("user_id required");
    try { await api.post(`/orgs/${orgId}/members`, newUser); toast.success("Member added"); setNewUser({ user_id: "", role: roles[0] || "org_manager" }); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (uid) => {
    if (!confirm(`Remove ${uid} from this organisation?`)) return;
    try { await api.delete(`/orgs/${orgId}/members/${uid}`); toast.success("Removed"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="ws-members">
      <div className="border border-border bg-card p-3 mb-3 flex gap-2 flex-wrap items-center">
        <Input placeholder="user_id" value={newUser.user_id} onChange={(e) => setNewUser({ ...newUser, user_id: e.target.value })} className="max-w-xs" data-testid="add-member-uid" />
        <select className="h-9 border border-border bg-background px-2 text-[12.5px] rounded-sm" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} data-testid="add-member-role">{roles.map(r => <option key={r} value={r}>{r}</option>)}</select>
        <Button size="sm" onClick={add} data-testid="add-member-submit">Add</Button>
      </div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-muted/40 text-left"><tr><th className="p-2.5">User</th><th className="p-2.5">Email</th><th className="p-2.5">Org role</th><th className="p-2.5">Platform role</th><th className="p-2.5">Added</th><th className="p-2.5"></th></tr></thead>
          <tbody className="divide-y divide-border">
            {members.map(m => (
              <tr key={m.user_id} data-testid={`ws-member-${m.user_id}`}>
                <td className="p-2.5">{m.user?.name || m.user_id}</td>
                <td className="p-2.5 mono text-[11px]">{m.user?.email || "—"}</td>
                <td className="p-2.5"><span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">{m.role}</span></td>
                <td className="p-2.5 mono text-[10.5px] uppercase text-muted-foreground">{m.user?.role || "—"}</td>
                <td className="p-2.5 mono text-[10.5px]">{(m.added_at || "").slice(0,10)}</td>
                <td className="p-2.5 text-right"><Button size="sm" variant="ghost" onClick={() => remove(m.user_id)}>Remove</Button></td>
              </tr>
            ))}
            {members.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground text-sm">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AnalyticsTab = ({ a }) => (
  <div className="grid lg:grid-cols-2 gap-4">
    <div className="border border-border bg-card">
      <div className="p-3 border-b border-border kicker">Top API paths · 30d</div>
      <table className="w-full text-[13px]">
        <thead className="bg-muted/40 text-left"><tr><th className="p-2.5">Path</th><th className="p-2.5">Calls</th><th className="p-2.5">OK</th></tr></thead>
        <tbody className="divide-y divide-border">
          {(a?.top_api_paths || []).map((r,i) => <tr key={i}><td className="p-2.5 mono text-[11px]">{r.path}</td><td className="p-2.5">{r.count}</td><td className="p-2.5">{r.ok}</td></tr>)}
          {(a?.top_api_paths || []).length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground text-sm">No API activity in period.</td></tr>}
        </tbody>
      </table>
    </div>
    <div className="border border-border bg-card">
      <div className="p-3 border-b border-border kicker">Certificate outcomes</div>
      <table className="w-full text-[13px]">
        <thead className="bg-muted/40 text-left"><tr><th className="p-2.5">Outcome</th><th className="p-2.5">Count</th></tr></thead>
        <tbody className="divide-y divide-border">
          {(a?.certificate_outcomes || []).map((r,i) => <tr key={i}><td className="p-2.5 uppercase mono text-[11px]">{r.outcome}</td><td className="p-2.5">{r.count}</td></tr>)}
          {(a?.certificate_outcomes || []).length === 0 && <tr><td colSpan={2} className="p-4 text-center text-muted-foreground text-sm">No certificates counter-signed by this organisation yet.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

const SettingsTab = ({ org, orgId, onSaved }) => {
  const [f, setF] = useState({
    logo_url: org?.branding?.logo_url || "",
    primary_colour: org?.branding?.primary_colour || "#0f1626",
    accent_colour: org?.branding?.accent_colour || "#D4AF37",
    tagline: org?.branding?.tagline || "",
    custom_email_footer: org?.branding?.custom_email_footer || "",
    custom_certificate_footer: org?.branding?.custom_certificate_footer || "",
  });
  const save = async () => {
    try { await api.patch(`/orgs/${orgId}`, { branding: f }); toast.success("Branding saved"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="max-w-2xl" data-testid="ws-settings">
      <div className="kicker mb-3">Branding</div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Logo URL"           span={2}><Input value={f.logo_url} onChange={(e) => setF({ ...f, logo_url: e.target.value })} placeholder="https://..." className="h-9 text-[12.5px]" /></F>
        <F label="Primary colour"     ><Input value={f.primary_colour} onChange={(e) => setF({ ...f, primary_colour: e.target.value })} className="h-9 text-[12.5px] font-mono" /></F>
        <F label="Accent colour"      ><Input value={f.accent_colour} onChange={(e) => setF({ ...f, accent_colour: e.target.value })} className="h-9 text-[12.5px] font-mono" /></F>
        <F label="Tagline"            span={2}><Input value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} className="h-9 text-[12.5px]" /></F>
        <F label="Certificate footer" span={2}><Input value={f.custom_certificate_footer} onChange={(e) => setF({ ...f, custom_certificate_footer: e.target.value })} className="h-9 text-[12.5px]" /></F>
        <F label="Email footer"       span={2}><Input value={f.custom_email_footer} onChange={(e) => setF({ ...f, custom_email_footer: e.target.value })} className="h-9 text-[12.5px]" /></F>
      </div>
      <div className="mt-4"><Button size="sm" onClick={save} data-testid="save-branding">Save branding</Button></div>
      <div className="mt-8 border border-border p-4 bg-muted/30 text-[12.5px]">
        <div className="kicker mb-2">Preview</div>
        <div className="p-4 rounded-sm" style={{ background: `linear-gradient(90deg, ${f.accent_colour}25, transparent)` }}>
          <div className="mono text-[9.5px] uppercase tracking-widest" style={{ color: f.accent_colour }}>{org?.industry}</div>
          <div className="text-lg font-semibold" style={{ color: f.primary_colour }}>{org?.name}</div>
          {f.tagline && <div className="text-[12.5px] text-muted-foreground">{f.tagline}</div>}
        </div>
      </div>
    </div>
  );
};

const AuditTab = ({ rows }) => (
  <div className="border border-border bg-card overflow-x-auto" data-testid="ws-audit">
    <table className="w-full text-[12.5px]">
      <thead className="bg-muted/40 text-left"><tr><th className="p-2.5">Time</th><th className="p-2.5">Actor</th><th className="p-2.5">Action</th><th className="p-2.5">Target</th></tr></thead>
      <tbody className="divide-y divide-border">
        {rows.map((r,i) => (
          <tr key={i}>
            <td className="p-2.5 mono text-[10.5px]">{(r.ts || "").slice(0,19).replace("T"," ")}</td>
            <td className="p-2.5 mono text-[10.5px]">{r.actor_id}</td>
            <td className="p-2.5 mono text-[10.5px] uppercase">{r.action}</td>
            <td className="p-2.5 mono text-[10.5px]">{r.target_type} · {r.target_id}</td>
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground text-sm">No audit events for this organisation yet.</td></tr>}
      </tbody>
    </table>
  </div>
);

const F = ({ label, span, children }) => (
  <div className={span === 2 ? "col-span-2" : ""}>
    <div className="mono text-[9.5px] uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
    {children}
  </div>
);
